/**
 * User Role Context with Caching + Realtime Permission Sync
 * P0 Fix: Added Realtime subscription on user_roles to detect permission changes immediately
 * P1 Fix: Reduced staleTime to 60s for faster permission propagation
 */

import { createContext, useContext, ReactNode, useMemo, useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./AuthContext";
import { structuredLogger } from "@/lib/logging";
import { logAuditEvent } from "@/lib/auditLog";

export type UserRole = 'app_admin' | 'org_admin' | 'coach' | 'viewer' | 'player_viewer' | 'parent' | null;

export interface UserRoleInfo {
  role: UserRole;
  organizationId: string | null;
  isLoading: boolean;
  isAppAdmin: boolean;
  isOrgAdmin: boolean;
  isCoach: boolean;
  isViewer: boolean;
  isPlayerViewer: boolean;
  isParent: boolean;
  approvalStatus: string | null;
  status: string | null;
  refetch: () => void;
  /** The role actually granted in the database (never affected by "view as"). */
  actualRole: UserRole;
  /** True when the signed-in user really is an app admin. */
  isRealAppAdmin: boolean;
  /** Role currently being previewed by an app admin, or null. */
  viewAsRole: UserRole;
  /** UI-only role preview. Admin-only, and can only downgrade, never elevate. */
  setViewAsRole: (role: UserRole) => void;
  /** Account currently being previewed by an app admin, or null. */
  impersonatedUser: ImpersonationTarget | null;
  /** Start a UI-only preview of a specific account. Audit-logged. */
  startImpersonation: (target: ImpersonationTarget) => Promise<void>;
  /** End the preview and return to the admin's own view. Audit-logged. */
  stopImpersonation: () => Promise<void>;
}

/** The account an app admin is previewing navigation and permissions for. */
export interface ImpersonationTarget {
  userId: string;
  email: string;
  fullName: string | null;
  role: UserRole;
  organizationId: string | null;
}

const CURRENT_ORG_KEY = "gg-current-org-id";
const GLOBAL_VIEW_VALUE = "__all__";
const VIEW_AS_KEY = "gg-view-as-role";
const VIEW_AS_EVENT = "gg-view-as-role-change";
const IMPERSONATE_KEY = "gg-impersonated-user";
const IMPERSONATE_EVENT = "gg-impersonated-user-change";

/**
 * Roles an app admin may preview in the UI.
 * Scoped to shipped personas only — Coach, Parent and Viewer surfaces are
 * legacy (pre-pivot team model) and have no implemented experience, so they
 * are not previewable.
 */
export const VIEW_AS_ROLES: Exclude<UserRole, null>[] = [
  "player_viewer",
  "org_admin",
];

export const ROLE_LABELS: Record<Exclude<UserRole, null>, string> = {
  app_admin: "App Admin",
  org_admin: "Institution Org Admin (gated)",
  coach: "Coach",
  viewer: "Viewer",
  player_viewer: "Learner",
  parent: "Parent",
};

const readViewAsRole = (): UserRole => {
  try {
    const stored = localStorage.getItem(VIEW_AS_KEY) as UserRole | null;
    return stored && VIEW_AS_ROLES.includes(stored as Exclude<UserRole, null>) ? stored : null;
  } catch {
    return null;
  }
};

const readImpersonation = (): ImpersonationTarget | null => {
  try {
    const raw = localStorage.getItem(IMPERSONATE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ImpersonationTarget;
    // Never allow a stored payload to grant app_admin — previews only ever downgrade.
    if (!parsed?.userId || parsed.role === "app_admin") return null;
    return parsed;
  } catch {
    return null;
  }
};

const defaultRoleInfo: UserRoleInfo = {
  role: null,
  organizationId: null,
  isLoading: true,
  isAppAdmin: false,
  isOrgAdmin: false,
  isCoach: false,
  isViewer: false,
  isPlayerViewer: false,
  isParent: false,
  approvalStatus: null,
  status: null,
  refetch: () => {},
  actualRole: null,
  isRealAppAdmin: false,
  viewAsRole: null,
  setViewAsRole: () => {},
  impersonatedUser: null,
  startImpersonation: async () => {},
  stopImpersonation: async () => {},
};

const UserRoleContext = createContext<UserRoleInfo>(defaultRoleInfo);

interface RoleData {
  role: UserRole;
  organizationId: string | null;
  approvalStatus: string | null;
  status: string | null;
}

/**
 * Writes both the dedicated impersonation log row and a standard audit event so
 * the action shows up in the security audit trail alongside other sensitive actions.
 */
async function recordImpersonation(
  adminId: string,
  target: ImpersonationTarget,
  action: "start" | "stop",
) {
  try {
    await supabase.from("impersonation_logs").insert({
      admin_id: adminId,
      impersonated_user_id: target.userId,
      action: action === "start" ? "impersonation_started" : "impersonation_ended",
    });
  } catch (err) {
    structuredLogger.error("Impersonation log insert failed", {
      action,
      error: err instanceof Error ? err.message : "unknown",
    });
  }
  await logAuditEvent({
    orgId: target.organizationId,
    actorUserId: adminId,
    actorRole: "app_admin",
    actionType: action === "start" ? "impersonation_started" : "impersonation_ended",
    entityType: "user",
    entityId: target.userId,
    metadata: { email: target.email, previewedRole: target.role },
  });
}

export const UserRoleProvider = ({ children }: { children: ReactNode }) => {
  const { user, isLoading: authLoading } = useAuth();
  const queryClient = useQueryClient();
  const [currentOrgId, setCurrentOrgId] = useState<string | null>(() => {
    const stored = localStorage.getItem(CURRENT_ORG_KEY);
    // Return null for global view (App Admin)
    return stored === GLOBAL_VIEW_VALUE ? null : stored;
  });

  // Admin-only "view as role" preview (UI scoping only — RLS still governs data).
  const [viewAsRole, setViewAsRoleState] = useState<UserRole>(() => readViewAsRole());
  // Admin-only "preview as this account" — UI scoping plus a full audit trail.
  const [impersonatedUser, setImpersonatedUserState] = useState<ImpersonationTarget | null>(
    () => readImpersonation(),
  );

  useEffect(() => {
    const sync = () => setImpersonatedUserState(readImpersonation());
    window.addEventListener("storage", sync);
    window.addEventListener(IMPERSONATE_EVENT, sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener(IMPERSONATE_EVENT, sync);
    };
  }, []);

  useEffect(() => {
    const sync = () => setViewAsRoleState(readViewAsRole());
    window.addEventListener("storage", sync);
    window.addEventListener(VIEW_AS_EVENT, sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener(VIEW_AS_EVENT, sync);
    };
  }, []);

  const setViewAsRole = useMemo(
    () => (next: UserRole) => {
      try {
        if (next) localStorage.setItem(VIEW_AS_KEY, next);
        else localStorage.removeItem(VIEW_AS_KEY);
      } catch {
        /* storage unavailable — preview stays in-memory */
      }
      setViewAsRoleState(next);
      window.dispatchEvent(new Event(VIEW_AS_EVENT));
      queryClient.invalidateQueries();
    },
    [queryClient],
  );


  const startImpersonation = useMemo(
    () => async (target: ImpersonationTarget) => {
      if (!user?.id || target.role === "app_admin") return;
      try {
        localStorage.setItem(IMPERSONATE_KEY, JSON.stringify(target));
      } catch {
        /* storage unavailable — preview stays in-memory */
      }
      setImpersonatedUserState(target);
      window.dispatchEvent(new Event(IMPERSONATE_EVENT));
      await recordImpersonation(user.id, target, "start");
      queryClient.invalidateQueries();
    },
    [user?.id, queryClient],
  );

  const stopImpersonation = useMemo(
    () => async () => {
      const target = impersonatedUser;
      try {
        localStorage.removeItem(IMPERSONATE_KEY);
      } catch {
        /* ignore */
      }
      setImpersonatedUserState(null);
      window.dispatchEvent(new Event(IMPERSONATE_EVENT));
      if (user?.id && target) await recordImpersonation(user.id, target, "stop");
      queryClient.invalidateQueries();
    },
    [user?.id, impersonatedUser, queryClient],
  );

  // Listen for org switches via localStorage (cross-tab sync)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === CURRENT_ORG_KEY) {
        const newValue = e.newValue === GLOBAL_VIEW_VALUE ? null : e.newValue;
        if (newValue !== currentOrgId) {
          setCurrentOrgId(newValue);
          // P0 Fix: Invalidate role cache on org switch from other tabs
          queryClient.invalidateQueries({ queryKey: ['user-role'] });
        }
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [currentOrgId, queryClient]);

  // P0 Fix: Realtime subscription on user_roles table for immediate permission revocation
  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel('user-role-changes')
      .on(
        'postgres_changes',
        {
          event: '*', // INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'user_roles',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          structuredLogger.info('[Permission Sync] Role change detected', { eventType: payload.eventType });
          // Immediately invalidate role cache on any role change
          queryClient.invalidateQueries({ queryKey: ['user-role'] });
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          structuredLogger.info('[Permission Sync] Profile change detected');
          // Invalidate on profile status/approval changes
          queryClient.invalidateQueries({ queryKey: ['user-role'] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, queryClient]);

  const { data: roleData, isLoading: roleLoading, refetch, isFetched, error: roleError } = useQuery({
    queryKey: ['user-role', user?.id, currentOrgId],
    queryFn: async (): Promise<RoleData> => {
      if (!user) {
        return { role: null, organizationId: null, approvalStatus: null, status: null };
      }

      structuredLogger.info('[UserRoleContext] Starting role fetch', { userId: user.id });

      // Get profile for approval status and user status
      // Wrapped in try/catch to handle missing profile gracefully
      let profile: { approval_status: string | null; organization_id: string | null; status: string | null } | null = null;
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('approval_status, organization_id, status')
          .eq('user_id', user.id)
          .maybeSingle();
        
        if (error) {
          structuredLogger.warn('Profile lookup failed, using defaults', { userId: user.id, error: error.message });
        } else {
          profile = data;
        }
      } catch (err) {
        structuredLogger.error('Profile lookup exception', { userId: user.id });
      }

      // Get user roles from table - retry once on empty result
      const { data: roles, error: rolesError } = await supabase
        .from('user_roles')
        .select('role, organization_id')
        .eq('user_id', user.id);

      if (rolesError) {
        structuredLogger.error('Failed to fetch user roles', { userId: user.id, error: rolesError.message });
      }

      structuredLogger.info('[UserRoleContext] Fetched roles from DB', { 
        userId: user.id, 
        rolesCount: roles?.length ?? 0,
        roles: roles?.map(r => ({ role: r.role, orgId: r.organization_id })),
        hasError: !!rolesError
      });

      let role: UserRole = null;
      let organizationId: string | null = null;

      if (roles && roles.length > 0) {
        // Prioritize roles
        const appAdminRole = roles.find(r => r.role === 'app_admin');
        const orgAdminRole = roles.find(r => r.role === 'org_admin');
        const coachRole = roles.find(r => r.role === 'coach');
        const viewerRole = roles.find(r => r.role === 'viewer');
        const playerViewerRole = roles.find(r => r.role === 'player_viewer');
        const parentRole = roles.find(r => r.role === 'parent');

        if (appAdminRole) {
          role = 'app_admin';
          organizationId = profile?.organization_id || null;
        } else if (orgAdminRole) {
          role = 'org_admin';
          organizationId = orgAdminRole.organization_id;
        } else if (coachRole) {
          role = 'coach';
          // For multi-org coaches, check localStorage for current org selection
          const coachOrgIds = roles
            .filter(r => r.role === 'coach')
            .map(r => r.organization_id)
            .filter((id): id is string => id !== null);
          
          if (currentOrgId && coachOrgIds.includes(currentOrgId)) {
            organizationId = currentOrgId;
          } else if (coachOrgIds.length > 0) {
            organizationId = coachOrgIds[0];
          }
        } else if (viewerRole) {
          role = 'viewer';
          organizationId = viewerRole.organization_id;
        } else if (playerViewerRole) {
          role = 'player_viewer';
          organizationId = playerViewerRole.organization_id;
        } else if (parentRole) {
          role = 'parent';
          organizationId = parentRole.organization_id;
        }
      } else {
        // SECURITY: Do NOT trust user_metadata for role assignment
        // user_metadata can be modified by the user themselves
        // Only player_viewer (lowest privilege) is allowed as fallback
        // and only if the user has no other roles in the database
        const userMetaRole = user.user_metadata?.role as string | undefined;
        if (userMetaRole === 'player_viewer') {
          // Log this for security monitoring - frequent occurrences may indicate abuse
          structuredLogger.debug('[Security] Using metadata fallback for player_viewer', { userId: user.id });
          role = 'player_viewer';
        }
        // NEVER allow app_admin, org_admin, coach, or other privileged roles from metadata
      }

      return {
        role,
        organizationId,
        approvalStatus: profile?.approval_status || null,
        status: profile?.status || null,
      };
    },
    enabled: !authLoading && !!user,
    staleTime: 60 * 1000, // P1 Fix: Reduced to 60 seconds for faster permission propagation
    gcTime: 2 * 60 * 1000, // 2 minutes
    refetchOnWindowFocus: true, // P1-5 Fix: Enable refetch on focus for permission data
  });

  const value = useMemo((): UserRoleInfo => {
    const isLoading = authLoading || roleLoading;
    const actualRole = roleData?.role ?? null;
    const isRealAppAdmin = actualRole === 'app_admin';
    // "View as" is a UI-only preview and can only downgrade an app admin.
    const activeViewAs = isRealAppAdmin ? viewAsRole : null;
    const activeImpersonation = isRealAppAdmin ? impersonatedUser : null;
    // Impersonating a specific account wins over the generic "view as role" preview.
    const role = activeImpersonation?.role ?? activeViewAs ?? actualRole;

    return {
      role,
      organizationId: activeImpersonation
        ? activeImpersonation.organizationId
        : roleData?.organizationId ?? null,
      isLoading,
      isAppAdmin: role === 'app_admin',
      isOrgAdmin: role === 'org_admin',
      isCoach: role === 'coach',
      isViewer: role === 'viewer',
      isPlayerViewer: role === 'player_viewer',
      isParent: role === 'parent',
      approvalStatus: roleData?.approvalStatus ?? null,
      status: roleData?.status ?? null,
      actualRole,
      isRealAppAdmin,
      viewAsRole: activeViewAs,
      setViewAsRole,
      impersonatedUser: activeImpersonation,
      startImpersonation,
      stopImpersonation,
      refetch: () => {
        queryClient.invalidateQueries({ queryKey: ['user-role'] });
        refetch();
      },
    };
  }, [
    authLoading,
    roleLoading,
    roleData,
    queryClient,
    refetch,
    viewAsRole,
    setViewAsRole,
    impersonatedUser,
    startImpersonation,
    stopImpersonation,
  ]);


  return (
    <UserRoleContext.Provider value={value}>
      {children}
    </UserRoleContext.Provider>
  );
};

export const useUserRoleContext = () => useContext(UserRoleContext);
